import { supabase } from '../lib/supabase';
import { EditorClip } from '../components/video-editor/types';

export type HighlightStatus = 
  | 'saved'        // Created, not processed
  | 'queued'       // Job queued
  | 'processing'   // stitch_clips running
  | 'stitched'     // Base video ready, can add overlays
  | 'enhancing'    // video_overlay_enhancements running
  | 'completed'    // Final video ready (with or without overlays)
  | 'failed';

export interface VideoHighlight {
  id: string;
  match_id: string;
  name: string;
  segments_data: EditorClip[];
  status: HighlightStatus;
  player_ids?: string[] | null;           // Optional player UUIDs this highlight is associated with
  output_url?: string | null;              // Base video (stitched) URL
  output_storage_path?: string | null;      // Base video (stitched) storage path
  file_size_bytes?: number | null;         // Base video (stitched) file size
  enhanced_url?: string | null;             // Enhanced video (with overlays) URL
  enhanced_storage_path?: string | null;    // Enhanced video (with overlays) storage path
  enhanced_file_size_bytes?: number | null; // Enhanced video (with overlays) file size
  error_message?: string | null;
  segment_effects_config?: any | null;  // JSONB: Segment-level effects (zoom, pan, speed, transitions)
  overlay_config?: any | null;          // JSONB: Video-level overlays (score, watermark, animations)
  created_by: string;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface CreateHighlightInput {
  matchId: string;
  name: string;
  segments: EditorClip[];
  createdBy: string;
  /** When saving from video editor with player filter: UUIDs of selected players. Omit or empty = do not associate. */
  player_ids?: string[] | null;
}

export interface UpdateHighlightInput {
  name?: string;
  segments?: EditorClip[];
  player_ids?: string[] | null;
  status?: HighlightStatus;
  outputUrl?: string | null;
  outputStoragePath?: string | null;
  fileSizeBytes?: number | null;
  enhancedUrl?: string | null;
  enhancedStoragePath?: string | null;
  enhancedFileSizeBytes?: number | null;
  errorMessage?: string | null;
  segmentEffectsConfig?: any | null;  // NEW: Segment-level effects config
  overlayConfig?: any | null;         // NEW: Overlay config
  updatedBy: string;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const table = 'video_highlights';

export const videoHighlightsService = {
  async getHighlightsByMatchId(matchId: string): Promise<ServiceResult<VideoHighlight[]>> {
    try {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('updated_at', { ascending: false }); // Order by latest updated, most recent first

      if (error) {
        console.error('Error fetching highlights:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: (data as VideoHighlight[]) || [] };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('getHighlightsByMatchId failed:', message);
      return { success: false, error: message };
    }
  },

  async getHighlightById(id: string): Promise<ServiceResult<VideoHighlight>> {
    try {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching highlight:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as VideoHighlight };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('getHighlightById failed:', message);
      return { success: false, error: message };
    }
  },

  async createHighlight(input: CreateHighlightInput): Promise<ServiceResult<VideoHighlight>> {
    try {
      const row: Record<string, unknown> = {
        match_id: input.matchId,
        name: input.name,
        segments_data: input.segments,
        status: 'saved',
        created_by: input.createdBy,
        updated_by: input.createdBy,
      };
      if (input.player_ids !== undefined && input.player_ids !== null && input.player_ids.length > 0) {
        row.player_ids = input.player_ids;
      }
      const { data, error } = await supabase
        .from(table)
        .insert(row)
        .select()
        .single();

      if (error) {
        console.error('Error creating highlight:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as VideoHighlight };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('createHighlight failed:', message);
      return { success: false, error: message };
    }
  },

  async updateHighlight(id: string, updates: UpdateHighlightInput): Promise<ServiceResult<VideoHighlight>> {
    try {
      const payload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
        updated_by: updates.updatedBy,
      };

      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.segments !== undefined) payload.segments_data = updates.segments;
      if (updates.player_ids !== undefined) payload.player_ids = updates.player_ids;
      if (updates.status !== undefined) payload.status = updates.status;
      if (updates.outputUrl !== undefined) payload.output_url = updates.outputUrl;
      if (updates.outputStoragePath !== undefined) payload.output_storage_path = updates.outputStoragePath;
      if (updates.fileSizeBytes !== undefined) payload.file_size_bytes = updates.fileSizeBytes;
      if (updates.enhancedUrl !== undefined) payload.enhanced_url = updates.enhancedUrl;
      if (updates.enhancedStoragePath !== undefined) payload.enhanced_storage_path = updates.enhancedStoragePath;
      if (updates.enhancedFileSizeBytes !== undefined) payload.enhanced_file_size_bytes = updates.enhancedFileSizeBytes;
      if (updates.errorMessage !== undefined) payload.error_message = updates.errorMessage;
      if (updates.segmentEffectsConfig !== undefined) payload.segment_effects_config = updates.segmentEffectsConfig;
      if (updates.overlayConfig !== undefined) payload.overlay_config = updates.overlayConfig;

      const { data, error } = await supabase
        .from(table)
        .update(payload)
        .eq('id', id)
        .eq('is_active', true)
        .select()
        .single();

      if (error) {
        console.error('Error updating highlight:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as VideoHighlight };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('updateHighlight failed:', message);
      return { success: false, error: message };
    }
  },

  async deleteHighlight(id: string, userId: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from(table)
        .update({
          is_active: false,
          updated_by: userId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('Error deleting highlight:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('deleteHighlight failed:', message);
      return { success: false, error: message };
    }
  },

  async updateHighlightIsActive(
    id: string,
    isActive: boolean,
    userId: string
  ): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from(table)
        .update({
          is_active: isActive,
          updated_by: userId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating highlight is_active:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('updateHighlightIsActive failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Get tournament ID from match
   */
  async getMatchTournamentId(matchId: string): Promise<ServiceResult<string>> {
    try {
      const { data, error } = await supabase
        .from('matches')
        .select('tournament_id')
        .eq('id', matchId)
        .single();

      if (error) {
        console.error('Error fetching match tournament:', error);
        return { success: false, error: error.message };
      }

      if (!data?.tournament_id) {
        return { success: false, error: 'Match does not have a tournament_id' };
      }

      return { success: true, data: data.tournament_id };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('getMatchTournamentId failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Build output path for highlight video
   * Format: highlights/{tournamentId}/{matchId}/{highlightId}/highlight.mp4
   */
  buildOutputPath(tournamentId: string, matchId: string, highlightId: string): string {
    return `highlights/${tournamentId}/${matchId}/${highlightId}/highlight.mp4`;
  },

  /**
   * Get the best available video URL for a highlight
   * Priority: enhanced_url > output_url > constructed from enhanced_storage_path > constructed from output_storage_path
   */
  getHighlightVideoUrl(highlight: VideoHighlight): string | null {
    // Prefer enhanced URL if available
    if (highlight.enhanced_url) {
      return highlight.enhanced_url;
    }
    // Fallback to base URL
    if (highlight.output_url) {
      return highlight.output_url;
    }
    // Construct from enhanced storage path if available
    if (highlight.enhanced_storage_path) {
      return `https://cdn.capture.kridah.com/${highlight.enhanced_storage_path}`;
    }
    // Construct from base storage path if available
    if (highlight.output_storage_path) {
      return `https://cdn.capture.kridah.com/${highlight.output_storage_path}`;
    }
    return null;
  },

  /**
   * Get the best available storage path for a highlight
   * Priority: enhanced_storage_path > output_storage_path
   */
  getHighlightStoragePath(highlight: VideoHighlight): string | null {
    return highlight.enhanced_storage_path || highlight.output_storage_path || null;
  },

  /**
   * Get the best available file size for a highlight
   * Priority: enhanced_file_size_bytes > file_size_bytes
   */
  getHighlightFileSize(highlight: VideoHighlight): number | null {
    return highlight.enhanced_file_size_bytes ?? highlight.file_size_bytes ?? null;
  },

  /**
   * Update highlight status (convenience method)
   */
  async updateHighlightStatus(
    id: string,
    status: HighlightStatus,
    userId: string,
    outputUrl?: string | null,
    outputStoragePath?: string | null,
    fileSizeBytes?: number | null,
    errorMessage?: string | null
  ): Promise<ServiceResult<VideoHighlight>> {
    return this.updateHighlight(id, {
      status,
      outputUrl,
      outputStoragePath,
      fileSizeBytes,
      errorMessage,
      updatedBy: userId,
    });
  },

  /**
   * Get all processing jobs for a highlight
   * Uses the highlight_id column for direct, efficient querying
   * Queries video_processing_jobs directly - RLS policy will ensure user has access
   * Returns all jobs (stitch_clips, video_overlay_enhancements, etc.) for complete history
   */
  async getHighlightJobs(highlightId: string): Promise<ServiceResult<any[]>> {
    try {
      console.log('[getHighlightJobs] Querying for highlightId:', highlightId);
      
      // Query video_processing_jobs directly using highlight_id
      // The RLS policy video_processing_jobs_select_by_highlight will ensure
      // the user has access to the highlight's match
      const { data: jobs, error: jobError } = await supabase
        .from('video_processing_jobs')
        .select('*')
        .eq('highlight_id', highlightId)
        .eq('is_active', true)
        .order('created_at', { ascending: false }); // Most recent first

      console.log('[getHighlightJobs] Direct query result:', {
        jobCount: jobs?.length || 0,
        error: jobError?.message,
        errorCode: jobError?.code
      });

      if (jobError) {
        console.error('[getHighlightJobs] Error querying jobs:', jobError);
        return { success: false, error: jobError.message };
      }

      console.log('[getHighlightJobs] Final result:', jobs ? `${jobs.length} job(s) found` : 'No jobs found');
      return { success: true, data: jobs || [] };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[getHighlightJobs] Exception:', message, err);
      return { success: false, error: message };
    }
  },

  /**
   * @deprecated Use getHighlightJobs instead - returns all jobs for a highlight
   * Get processing job for a highlight (legacy method - returns only stitch_clips job)
   */
  async getHighlightJob(highlightId: string): Promise<ServiceResult<any>> {
    const result = await this.getHighlightJobs(highlightId);
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    // Return the most recent stitch_clips job for backward compatibility
    const stitchJob = result.data.find(job => job.job_type === 'stitch_clips');
    return { success: true, data: stitchJob || null };
  },

  /**
   * Process a highlight (create processing job)
   */
  async processHighlight(highlightId: string): Promise<ServiceResult<{ jobId: string; highlightId: string; status: string }>> {
    try {
      // Get Supabase URL and auth token
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        return { success: false, error: 'Supabase URL not configured' };
      }
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/process-highlight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ highlightId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        return { success: false, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('processHighlight failed:', message);
      return { success: false, error: message };
    }
  },
};

